import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';

export interface ConfirmActionDialogData {
  title: string;
  message: string;
  confirmLabel: string;
  loadingLabel?: string;
  confirmColor?: 'primary' | 'warn' | 'accent';
  /** When set, the dialog stays open, shows a spinner, and closes only after the action completes. */
  action?: () => Observable<unknown>;
}

@Component({
  selector: 'app-confirm-action-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatProgressSpinnerModule],
  template: `
    <h2 mat-dialog-title class="!text-lg font-semibold">{{ data.title }}</h2>
    <mat-dialog-content class="!text-sm text-gray-600 dark:text-gray-300">
      <p>{{ data.message }}</p>
      @if (errorMessage) {
        <p class="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
          {{ errorMessage }}
        </p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="!pb-4 !pt-2">
      <button mat-button type="button" [disabled]="loading" (click)="onCancel()">Cancel</button>
      <button
        mat-flat-button
        type="button"
        [color]="data.confirmColor || 'primary'"
        [disabled]="loading"
        (click)="onConfirm()"
      >
        @if (loading) {
          <span class="inline-flex items-center gap-2">
            <mat-spinner diameter="18" />
            {{ data.loadingLabel || 'Working…' }}
          </span>
        } @else {
          {{ data.confirmLabel }}
        }
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmActionDialogComponent {
  readonly data = inject<ConfirmActionDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ConfirmActionDialogComponent>);

  loading = false;
  errorMessage = '';

  onCancel(): void {
    if (this.loading) {
      return;
    }
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    if (this.loading) {
      return;
    }
    if (!this.data.action) {
      this.dialogRef.close(true);
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.data
      .action()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err: unknown) => {
          this.errorMessage = resolveApiErrorMessage(err) || 'Action failed. Please try again.';
        },
      });
  }
}
